// Module-level Voximplant Web SDK v5 controller.
//
// ── Why a singleton ────────────────────────────────────
// The softphone must survive Inertia navigations (the SDK
// connection + login + active call outlive any single
// page) and must be mounted ONCE at the app level so an
// agent receives inbound calls on every viewport (CON-82
// lesson — never nest the live connection inside a
// viewport branch). So the SDK lives here, at module
// scope, and React subscribes to it via `useVoximplant`
// (useSyncExternalStore). `Core.init` is called at most
// once.
//
// ── v5 ≠ v4 ────────────────────────────────────────────
// Core.init + module loaders, promise-based, Watchables.
// `client.connect({ node })` (node on CONNECT, not init).
// Auth: one-time-key bootstrap → renewable tokens, then
// `loginAccessToken` / `refreshTokens` on later loads.
// In-call: `hold()` (not v4 `setActive`), `sendDTMF()`,
// `muteMicrophone`/`unmuteMicrophone`.
//
// ── Testability ────────────────────────────────────────
// The real SDK is loaded through an injected `loadSdk`
// loader (default: `defaultSdkLoader`, which does the real
// Core.init wiring). Tests pass a fake loader implementing
// the same interfaces, so the orchestration — auth
// sequence, incoming-call state machine, watchable
// reconciliation — is exercised without a real WebRTC
// stack.

import type { Client, ClientState, ConnectionNode } from "@voximplant/websdk"
import type {
  Call,
  CallManager,
  CallState,
  CallDirection,
  CallEvent,
  CallManagerEvent,
  RejectMode,
} from "@voximplant/websdk/modules/call-manager"
import type {
  StreamModule,
  StreamHelper,
  DeviceTrackerHelper,
  LocalStream,
  RemoteStream,
  AudioRenderer,
} from "@voximplant/websdk/modules/stream"

import {
  microphoneErrorMessage,
  connectionErrorMessage,
  isAutoplayError,
  AUTOPLAY_BLOCKED_MESSAGE,
} from "@/lib/voice/errors"
import { isTerminalVoiceCall } from "@/lib/voice/call-record"
import { getDeviceToken } from "@/lib/voice/device-id"
import {
  loadTokens,
  saveTokens,
  clearTokens,
  fromSdkTokens,
  accessTokenUsable,
  refreshTokenUsable,
  type SdkLoginTokens,
} from "@/lib/voice/token-store"
import type {
  SoftphoneState,
  CallSnapshot,
  VoiceCallRecord,
  VoiceConfig,
  MicDevice,
} from "@/types/v2/voice"
import { getCsrfToken } from "@/lib/csrf"

// Failure-path logging only. A genuine connect/login or
// SDK-load failure stays invisible otherwise (the SDK lives
// behind a dynamic import + WebRTC + a token bootstrap), so
// we keep a single concise console.error on the failure
// paths. Verbose stage/info logging is intentionally absent.
function verr(stage: string, error: unknown): void {
  console.error(`[voice] ${stage}`, error)
}

// The SDK enum values the controller branches on. The
// default loader supplies the real enums; the fake supplies
// matching string members. Keeping them out of static
// imports means tests never touch the real module.
export interface VoxEnums {
  ConnectionNode: typeof ConnectionNode
  ClientEventDisconnected: string
  ClientDisconnectConnectionLost: string
  // Full client-state enum (Disconnected/Connecting/Connected/
  // Reconnecting/…). Available for connection-health checks;
  // mirrors the CallState entry below. (CON-151)
  ClientState: typeof ClientState
  CallManagerIncomingCall: CallManagerEvent
  // Full call-state enum. The controller branches on
  // `CallState.Reconnecting` to surface a dropped-media leg
  // (CON-151).
  CallState: typeof CallState
  CallEventConnected: CallEvent
  CallEventDisconnected: CallEvent
  CallEventFailed: CallEvent
  CallDirectionIncoming: CallDirection
  RejectModeDecline: RejectMode
  // The specific enum member (not the union) so
  // `createHelper` resolves to `DeviceTrackerHelper`.
  StreamHelperDeviceTracker: StreamHelper.DeviceTracker
}

// The assembled SDK surface the controller drives.
export interface VoxSdk {
  client: Client
  callManager: CallManager
  streamModule: StreamModule
  enums: VoxEnums
}

export type SdkLoader = () => Promise<VoxSdk>

export interface VoximplantClientDeps {
  loadSdk: SdkLoader
  // Posts the one-time login key to the Rails auth
  // endpoint and returns the server-computed hash.
  fetchAuthHash?: (loginKey: string) => Promise<string>
  deviceToken?: string
  now?: () => number
}

const INITIAL_STATE: SoftphoneState = {
  connection: "idle",
  connectionError: null,
  call: null,
  microphones: [],
  currentMicrophoneId: null,
  mediaError: null,
}

type Unwatch = () => void

export class VoximplantClient {
  private readonly deps: Required<
    Omit<VoximplantClientDeps, "fetchAuthHash">
  > & { fetchAuthHash: (loginKey: string) => Promise<string> }

  private state: SoftphoneState = INITIAL_STATE
  private listeners = new Set<() => void>()

  private sdk: VoxSdk | null = null
  private config: VoiceConfig | null = null
  private startPromise: Promise<void> | null = null

  // Active call bookkeeping.
  private call: Call | null = null
  // A server-driven outbound VoiceCall that arrived (via the
  // ActionCable broadcast) BEFORE the SDK rang the agent's
  // own leg. Stashed so `wireIncomingCalls` can attach that
  // leg as an outgoing call and auto-answer it, rather than
  // surfacing an answer/decline sheet for a call the agent
  // initiated.
  private pendingOutbound: VoiceCallRecord | null = null
  private callWatchers: Unwatch[] = []
  private renderers = new Map<string, AudioRenderer>()
  // Whether the active call has ever received remote media.
  // Once true, an empty `remoteStreams` set means the remote
  // audio leg dropped (RemoteMediaRemoved) — distinct from the
  // pre-connect state where no media has arrived yet. Reset
  // per call in `attachCall`/`detachCall`. (CON-151)
  private hadRemoteMedia = false
  private deviceTracker: DeviceTrackerHelper | null = null
  private micStream: LocalStream | null = null
  // Whether WE captured `micStream` via `createAudioStream`
  // (so we own it and must `close()` it to release the mic) vs.
  // borrowed it from the device tracker (the tracker owns its
  // own lifecycle). Drives `releaseMicStream` (CON-169).
  private micStreamOwned = false
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  // Auto-collapse timer for a normally-ended call. A FAILED
  // call has no such timer (it persists until dismissed); if a
  // failed terminal arrives while this is pending, we cancel it.
  private settleTimer: ReturnType<typeof setTimeout> | null = null
  // Whether the `online` recovery listener has been bound. The
  // SDK's autoReconnect only covers transient losses; a full
  // network blackout leaves the softphone stuck in `error` with
  // nothing retrying once the network returns. We bind one
  // `online` listener (on the first `start`) to auto-recover.
  // (CON-179)
  private onlineListenerBound = false
  // True while an attemptReconnect() disconnect/connect/login
  // sequence is in flight, so overlapping triggers (SDK
  // disconnect, manual button, `online` event) can't start a
  // second concurrent attempt. (CON-179)
  private reconnecting = false

  constructor(deps: VoximplantClientDeps) {
    this.deps = {
      loadSdk: deps.loadSdk,
      deviceToken: deps.deviceToken ?? safeDeviceToken(),
      now: deps.now ?? (() => Date.now()),
      fetchAuthHash: deps.fetchAuthHash ?? defaultFetchAuthHash,
    }
  }

  // ── External store interface (for useSyncExternalStore) ──

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getState = (): SoftphoneState => this.state

  private setState(patch: Partial<SoftphoneState>): void {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((l) => l())
  }

  private patchCall(patch: Partial<CallSnapshot>): void {
    if (!this.state.call) return
    this.setState({ call: { ...this.state.call, ...patch } })
  }

  // ── Lifecycle ───────────────────────────────────────────

  // Idempotent: connecting once per config. Safe to call on
  // every page mount; later calls are no-ops while a start
  // is in flight or already succeeded for the same username.
  start(config: VoiceConfig): Promise<void> {
    if (!config.enabled) {
      return Promise.resolve()
    }

    if (
      this.startPromise &&
      this.config?.username === config.username
    ) {
      return this.startPromise
    }

    this.config = config
    this.bindOnlineRecovery()
    this.startPromise = this.connectAndLogin(config).catch(
      (error) => {
        verr("start: FAILED — connect/login threw", error)
        this.setState({
          connection: "error",
          connectionError: connectionErrorMessage(error),
        })
        // Let a later start() retry.
        this.startPromise = null
        throw error
      },
    )
    return this.startPromise
  }

  private async connectAndLogin(config: VoiceConfig): Promise<void> {
    this.setState({ connection: "connecting", connectionError: null })

    const sdk = await this.ensureSdk()
    const username = this.fullUsername(config)

    const node = this.nodeFor(sdk, config.node)
    // autoReconnect: let the SDK rejoin the Voximplant Cloud on
    // a transient connection loss even with no active call.
    // Explicit (the SDK defaults it true, but the contract
    // matters too much to leave implicit). (CON-151)
    await sdk.client.connect({ node, autoReconnect: true })

    await this.login(sdk, username)

    this.setState({ connection: "connected", connectionError: null })
    this.setupDeviceTracker(sdk)
  }

  private async ensureSdk(): Promise<VoxSdk> {
    if (this.sdk) return this.sdk

    const sdk = await this.deps.loadSdk()
    this.sdk = sdk
    this.wireClientEvents(sdk)
    this.wireIncomingCalls(sdk)
    return sdk
  }

  private async login(sdk: VoxSdk, username: string): Promise<void> {
    const stored = loadTokens(username)

    // Renewable-token path: skip the OTK round-trip when a
    // usable access token (or a refreshable one) is on hand.
    if (stored) {
      if (accessTokenUsable(stored, this.deps.now())) {
        try {
          await sdk.client.loginAccessToken({
            username,
            accessToken: stored.accessToken,
            deviceToken: this.deps.deviceToken,
          })
          this.scheduleRefresh(sdk, username, stored.accessExpiresAt)
          return
        } catch {
          // Fall through to refresh / OTK.
        }
      }

      if (refreshTokenUsable(stored, this.deps.now())) {
        try {
          const refreshed = await sdk.client.refreshTokens({
            username,
            refreshToken: stored.refreshToken,
            deviceToken: this.deps.deviceToken,
          })
          this.persistTokens(username, refreshed)
          await sdk.client.loginAccessToken({
            username,
            accessToken: refreshed.accessToken,
            deviceToken: this.deps.deviceToken,
          })
          this.scheduleRefresh(
            sdk,
            username,
            this.deps.now() + refreshed.accessExpire * 1000,
          )
          return
        } catch {
          clearTokens(username)
          // Fall through to OTK bootstrap.
        }
      }
    }

    await this.bootstrapWithOneTimeKey(sdk, username)
  }

  // One-time-key bootstrap: requestOneTimeKey → POST the key
  // to Rails → loginOneTimeKey with the server hash →
  // persist the renewable tokens it returns.
  private async bootstrapWithOneTimeKey(
    sdk: VoxSdk,
    username: string,
  ): Promise<void> {
    const loginKey = await sdk.client.requestOneTimeKey({ username })
    const hash = await this.deps.fetchAuthHash(loginKey)

    const result = await sdk.client.loginOneTimeKey({
      username,
      hash,
      deviceToken: this.deps.deviceToken,
    })

    if (result.loginTokens) {
      this.persistTokens(username, result.loginTokens)
      this.scheduleRefresh(
        sdk,
        username,
        this.deps.now() + result.loginTokens.accessExpire * 1000,
      )
    }
  }

  private persistTokens(
    username: string,
    tokens: SdkLoginTokens,
  ): void {
    saveTokens(username, fromSdkTokens(tokens, this.deps.now()))
  }

  // Refresh ~1 minute before the access token expires. The
  // access token lives ~30 days — well past setTimeout's
  // 32-bit ceiling (~24.8 days), where an over-large delay
  // silently fires immediately and would spin a refresh
  // loop. So we chunk: wait at most MAX_TIMER, re-arm if
  // we're not actually due yet, and only refresh once the
  // real target time has passed.
  private scheduleRefresh(
    sdk: VoxSdk,
    username: string,
    accessExpiresAt: number,
  ): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer)

    const MAX_TIMER = 2_147_483_647 // 2^31 - 1 ms
    const lead = 60_000
    const target = accessExpiresAt - lead
    const remaining = target - this.deps.now()
    const delay = Math.min(Math.max(remaining, 5_000), MAX_TIMER)

    this.refreshTimer = setTimeout(() => {
      if (this.deps.now() < target) {
        // Not due yet — the wait was capped; re-arm.
        this.scheduleRefresh(sdk, username, accessExpiresAt)
      } else {
        void this.refresh(sdk, username)
      }
    }, delay)
  }

  private async refresh(sdk: VoxSdk, username: string): Promise<void> {
    const stored = loadTokens(username)
    if (!stored || !refreshTokenUsable(stored, this.deps.now())) {
      // Refresh window gone — re-bootstrap from scratch.
      try {
        await this.bootstrapWithOneTimeKey(sdk, username)
      } catch (error) {
        this.setState({
          connectionError: connectionErrorMessage(error),
        })
      }
      return
    }

    try {
      const refreshed = await sdk.client.refreshTokens({
        username,
        refreshToken: stored.refreshToken,
        deviceToken: this.deps.deviceToken,
      })
      this.persistTokens(username, refreshed)
      this.scheduleRefresh(
        sdk,
        username,
        this.deps.now() + refreshed.accessExpire * 1000,
      )
    } catch {
      clearTokens(username)
    }
  }

  private wireClientEvents(sdk: VoxSdk): void {
    sdk.client.addEventListener(
      sdk.enums.ClientEventDisconnected as never,
      (event: { payload?: { reason?: string } }) => {
        const lost =
          event?.payload?.reason ===
          sdk.enums.ClientDisconnectConnectionLost
        if (lost) {
          this.setState({ connection: "reconnecting" })
          void this.attemptReconnect(sdk)
        } else {
          this.setState({ connection: "idle" })
        }
      },
      {} as never,
    )
  }

  // Manual reconnect, driven by the sidebar StatusStrip. The
  // SDK auto-reconnects on a transient `ConnectionLost` (we
  // pass `autoReconnect: true` on connect), so this exists to
  // recover the terminal `error` state — re-run connect+login
  // with the stored config. No-ops when voice was never
  // configured.
  // Bind a one-shot `online` listener so the softphone recovers
  // by itself when the network comes back — the SDK's own
  // autoReconnect gives up after a full blackout and never
  // retries on its own. (CON-179)
  private bindOnlineRecovery(): void {
    if (this.onlineListenerBound) return
    if (typeof window === "undefined") return
    window.addEventListener("online", this.handleOnline)
    this.onlineListenerBound = true
  }

  private handleOnline = (): void => {
    if (!this.config) return
    const { connection } = this.state
    // Already healthy or mid-attempt — nothing to recover. An
    // attempt that is genuinely in flight is also guarded inside
    // attemptReconnect (`this.reconnecting`), so an `online`
    // racing the SDK disconnect handler can't double-run.
    if (connection === "connected" || connection === "connecting") {
      return
    }
    this.reconnect()
  }

  reconnect(): void {
    const config = this.config
    if (!config) return

    if (this.sdk) {
      this.setState({ connection: "reconnecting", connectionError: null })
      void this.attemptReconnect(this.sdk)
      return
    }

    // SDK never loaded (initial connect threw before ensureSdk
    // assigned it) — clear the settled start promise so start()
    // re-attempts instead of returning the failed one.
    this.startPromise = null
    void this.start(config).catch(() => {
      // Error already reflected in state.connectionError.
    })
  }

  private async attemptReconnect(sdk: VoxSdk): Promise<void> {
    const config = this.config
    if (!config) return
    // Guard against a second concurrent attempt: the SDK
    // disconnect handler, the manual Reconnect button, and the
    // `online` recovery listener can all fire at once. Running
    // two disconnect/connect/login sequences against the same
    // SDK instance corrupts its state. (CON-179)
    if (this.reconnecting) return
    this.reconnecting = true
    try {
      // After a full network blackout the SDK's own
      // autoReconnect exhausts its retries and leaves the client
      // wedged in a non-Disconnected-but-dead state. A bare
      // connect() on that instance can resolve WITHOUT rebuilding
      // the transport, so the following login() then throws
      // LoginInvalidStateError (DISCONNECTED). Tear the dead
      // connection down first so connect() establishes a fresh
      // transport. (CON-179)
      if (sdk.client.state.value !== sdk.enums.ClientState.Disconnected) {
        try {
          await sdk.client.disconnect()
        } catch {
          // Best-effort — disconnecting an already-dead client can
          // reject; the connect() below is what actually matters.
        }
      }
      await sdk.client.connect({
        node: this.nodeFor(sdk, config.node),
        autoReconnect: true,
      })
      await this.login(sdk, this.fullUsername(config))
      this.setState({ connection: "connected", connectionError: null })
    } catch (error) {
      this.setState({
        connection: "error",
        connectionError: connectionErrorMessage(error),
      })
    } finally {
      this.reconnecting = false
    }
  }

  // ── Devices ─────────────────────────────────────────────

  private setupDeviceTracker(sdk: VoxSdk): void {
    if (this.deviceTracker) return

    const tracker = sdk.streamModule.createHelper(
      sdk.enums.StreamHelperDeviceTracker,
    )
    this.deviceTracker = tracker
    tracker.enableTracker()

    tracker.microphones.watch(
      (mics) => this.setState({ microphones: toMicDevices(mics) }),
      { immediate: true },
    )
    tracker.currentMicrophone.watch(
      (mic) => this.setState({ currentMicrophoneId: mic?.id ?? null }),
      { immediate: true },
    )
  }

  selectMicrophone(deviceId: string): void {
    const tracker = this.deviceTracker
    if (!tracker) return
    const mic = tracker.microphones.value.find((m) => m.id === deviceId)
    if (mic) tracker.currentMicrophone.value = mic
    // Release the cached stream (closing the old device's mic
    // if we own it) so the next call captures the newly-selected
    // device.
    this.releaseMicStream()
  }

  // ── Incoming calls ──────────────────────────────────────

  private wireIncomingCalls(sdk: VoxSdk): void {
    sdk.callManager.addEventListener(
      sdk.enums.CallManagerIncomingCall,
      (event: { payload: { callId: string } }) => {
        const call = sdk.callManager.getCalls().get(event.payload.callId)
        if (!call) return

        // Server-driven outbound: the scenario rings the
        // agent's OWN leg, which the SDK delivers as an
        // IncomingCall. If the outbound broadcast already
        // landed, attach this as an outgoing call (phase
        // "connecting", NOT "incoming") and auto-answer it —
        // no answer/decline sheet for a call the agent
        // initiated.
        const pending = this.pendingOutbound
        if (pending) {
          this.pendingOutbound = null
          this.attachCall(sdk, call, "outgoing")
          this.patchCall({
            voiceCall: pending,
            remoteDisplayName:
              this.state.call?.remoteDisplayName ??
              pending.contact?.name ??
              null,
          })
          void this.answer()
          return
        }

        this.attachCall(sdk, call, "incoming")
      },
      {} as never,
    )
  }

  // Place an outbound call directly from the SDK. The common
  // outbound path is server-driven (the scenario rings the
  // agent, arriving as an IncomingCall) — this exists for
  // SDK-initiated dialling where the destination routes to
  // the outbound rule.
  async placeCall(destination: string): Promise<void> {
    const sdk = this.sdk
    if (!sdk) return
    const call = sdk.callManager.createCall(destination)
    this.attachCall(sdk, call, "outgoing")
    try {
      await this.ensureMicAttached(sdk, call)
      await call.start()
    } catch (error) {
      this.failCall(error)
    }
  }

  private attachCall(
    sdk: VoxSdk,
    call: Call,
    direction: "incoming" | "outgoing",
  ): void {
    // One call at a time — drop any stale wiring, and cancel a
    // pending auto-collapse so a prior call's settle timer can't
    // clear this fresh snapshot.
    this.detachCall()
    this.clearSettleTimer()
    this.call = call
    this.hadRemoteMedia = false

    this.setState({
      mediaError: null,
      call: {
        sdkCallId: call.id,
        direction,
        phase: direction === "incoming" ? "incoming" : "connecting",
        isMuted: call.isMicrophoneMuted.value,
        isOnHold: call.isOnHold.value,
        remoteDisplayName: call.remoteDisplayName.value,
        connectedAt: null,
        voiceCall: null,
        endReason: null,
        mediaState: "ok",
      },
    })

    this.callWatchers.push(
      call.remoteDisplayName.watch((name) =>
        this.patchCall({ remoteDisplayName: name }),
      ),
      call.isMicrophoneMuted.watch((muted) =>
        this.patchCall({ isMuted: muted }),
      ),
      call.isOnHold.watch((onHold) =>
        this.patchCall({ isOnHold: onHold }),
      ),
      call.remoteStreams.watch((streams) =>
        this.renderRemoteStreams(sdk, streams),
      ),
      // Surface the SDK's own mid-call recovery: a Call entering
      // `Reconnecting` (RTP/transport hiccup) flags the snapshot
      // as media-dropped; returning to `Connected` clears it.
      // (CON-151)
      call.state.watch((next) => this.reflectCallState(sdk, next)),
    )

    call.addEventListener(
      sdk.enums.CallEventConnected,
      () => {
        // For an INCOMING call, the agent answering IS the talk
        // start, so stamp the timer now. For an OUTGOING call this
        // fires when the agent's OWN leg connects (auto-answer) —
        // before the customer picks up — so don't start the timer
        // here; it's stamped when the `call.connected` broadcast
        // lands with `answeredAt` (see applyOutboundBroadcast).
        const startsTimer = this.state.call?.direction === "incoming"
        this.patchCall({
          phase: "active",
          ...(startsTimer ? {connectedAt: this.deps.now()} : {}),
        })
      },
      {} as never,
    )
    call.addEventListener(
      sdk.enums.CallEventDisconnected,
      () => this.endCall("ended"),
      {} as never,
    )
    call.addEventListener(
      sdk.enums.CallEventFailed,
      () => this.endCall("failed"),
      {} as never,
    )
  }

  async answer(): Promise<void> {
    const sdk = this.sdk
    const call = this.call
    if (!sdk || !call) return
    try {
      await this.ensureMicAttached(sdk, call)
      this.patchCall({ phase: "connecting" })
      await call.answer()
    } catch (error) {
      this.failCall(error)
    }
  }

  decline(): void {
    const sdk = this.sdk
    const call = this.call
    if (!sdk || !call) return
    call.reject(sdk.enums.RejectModeDecline)
    this.endCall("ended")
  }

  hangup(): void {
    this.call?.hangup()
    // The Disconnected event finalizes; this is the trigger.
  }

  toggleMute(): void {
    const call = this.call
    if (!call) return
    if (call.isMicrophoneMuted.value) {
      call.unmuteMicrophone()
    } else {
      call.muteMicrophone()
    }
  }

  async toggleHold(): Promise<void> {
    const call = this.call
    if (!call) return
    try {
      await call.hold(!call.isOnHold.value)
    } catch {
      // hold() throws if the call isn't connected; ignore.
    }
  }

  sendTone(tone: string): void {
    if (!tone) return
    this.call?.sendDTMF(tone)
  }

  // Merge a backend VoiceCall (from the ActionCable
  // broadcast) into the active call snapshot so the UI can
  // show the contact's name and reach the call panels.
  applyBroadcast(voiceCall: VoiceCallRecord): void {
    // The SERVER is the source of truth for whether a call
    // actually reached a connected customer. A `call.ended`
    // broadcast whose VoiceCall is `failed`/`missed` (or
    // never stamped `answeredAt`) means the customer never
    // connected — surface a terminal failure and tear the
    // overlay down even if the SDK's own Disconnected event
    // lags or never fires. (Do NOT trust the SDK agent-leg
    // connectedAt as "customer answered" — it's set on
    // auto-answer of the agent leg, before the customer
    // bridges.)
    if (this.isUnconnectedTerminalBroadcast(voiceCall)) {
      this.failFromBroadcast(voiceCall)
      return
    }

    if (voiceCall.direction === "outbound") {
      this.applyOutboundBroadcast(voiceCall)
      return
    }

    if (!this.state.call) return
    this.patchCall({
      voiceCall,
      remoteDisplayName:
        this.state.call.remoteDisplayName ??
        voiceCall.contact?.name ??
        null,
    })
  }

  // Reconcile the local call snapshot against the server's
  // authoritative live call after a (re)connect — the server
  // is the source of truth (CLAUDE.md). Fetched by the
  // softphone from `GET /v2/channels/voice/active_call` when
  // the ActionCable subscription (re)connects, healing state
  // that drifted while the WebSocket was down.
  //
  //   - A live call → merge it (heals a missed `call.connected`
  //     broadcast, flipping a stuck "Calling…" to active).
  //   - No live call (null) → tear down a stranded non-terminal
  //     snapshot, BUT only one that already has a backing
  //     VoiceCall id. A brand-new call whose record the server
  //     hasn't created yet has `voiceCall == null`; leaving it
  //     alone avoids nuking a call that's just starting.
  reconcileActiveCall(voiceCall: VoiceCallRecord | null): void {
    if (voiceCall) {
      this.applyBroadcast(voiceCall)
      return
    }

    const snapshot = this.state.call
    if (!snapshot) return

    const nonTerminal =
      snapshot.phase === "incoming" ||
      snapshot.phase === "connecting" ||
      snapshot.phase === "active"
    if (nonTerminal && snapshot.voiceCall != null) {
      // The server no longer knows about this call → it ended
      // while our WS was down. Hang up any zombie SDK leg and
      // collapse the snapshot through the normal ended path.
      this.call?.hangup()
      this.endCall("ended")
    }
  }

  // A `call.ended`/`failed` broadcast for a call that never
  // reached a connected customer: backend status is
  // `failed`/`missed`, or it ended without ever stamping
  // `answeredAt`. Only meaningful while there's an active
  // call snapshot or a pending outbound to tear down.
  private isUnconnectedTerminalBroadcast(
    voiceCall: VoiceCallRecord,
  ): boolean {
    const terminalStatus =
      voiceCall.status === "failed" || voiceCall.status === "missed"
    const endedUnanswered =
      voiceCall.endedAt != null && voiceCall.answeredAt == null
    return terminalStatus || endedUnanswered
  }

  // Drive the UI to a humane terminal-failure state and
  // guarantee teardown, independent of the SDK's own
  // Disconnected/Failed events (which may lag or never
  // arrive for a leg that never bridged). Also clears any
  // stashed pendingOutbound so a never-rung agent leg can't
  // leave the overlay stuck on "Calling…".
  private failFromBroadcast(voiceCall: VoiceCallRecord): void {
    this.pendingOutbound = null

    if (this.state.call) {
      this.patchCall({ voiceCall, endReason: "failed" })
    } else {
      // No SDK leg ever attached, but we still want to flash a
      // brief "couldn't be connected" so the click that
      // started the call gets feedback.
      this.setState({
        call: {
          sdkCallId: `failed-${voiceCall.id}`,
          direction:
            voiceCall.direction === "outbound" ? "outgoing" : "incoming",
          phase: "failed",
          isMuted: false,
          isOnHold: false,
          remoteDisplayName: voiceCall.contact?.name ?? null,
          connectedAt: null,
          voiceCall,
          endReason: "failed",
          mediaState: "ok",
        },
      })
    }
    // Lock the snapshot into the failed terminal. It persists
    // (no auto-collapse) until the user dismisses it, and a late
    // SDK Disconnected can't downgrade it (see endCall).
    this.endCall("failed")
  }

  // Server-driven outbound: the agent clicked Call, so their
  // own leg will ring as an SDK IncomingCall in a few seconds.
  // Render it as an OUTGOING "Calling…" state, never an
  // answer/decline sheet.
  private applyOutboundBroadcast(voiceCall: VoiceCallRecord): void {
    const snapshot = this.state.call

    // The agent's leg already attached. Merge the latest backend
    // state into the snapshot for EVERY phase — crucially the
    // `call.connected` broadcast (which stamps `answeredAt` when
    // the customer picks up) arrives while the agent leg is
    // already `active`, and that's what flips the UI out of
    // "Calling…" into the live call. If the leg is still sitting
    // in the incoming sheet, also auto-answer it.
    if (snapshot) {
      const wasIncoming = snapshot.phase === "incoming"
      // Anchor the call timer to CUSTOMER pickup, not the agent's
      // own leg. The `call.connected` broadcast is the first one
      // carrying `answeredAt`; stamp connectedAt then so the live
      // timer matches the server-authoritative duration.
      //
      // While still dialing (`answeredAt == null`) force connectedAt
      // back to null. This is the deterministic correction for the
      // "broadcast lands late" race: if the agent's SDK leg reached
      // `Connected` before this broadcast flipped direction to
      // outgoing, the incoming-path handler may have already stamped
      // connectedAt at agent-connect time. Clearing it here wipes
      // that premature stamp so the customer-connected broadcast can
      // re-stamp it correctly. Once stamped on customer pickup, leave
      // it (later broadcasts are idempotent).
      const connectedAtPatch =
        voiceCall.answeredAt == null
          ? {connectedAt: null}
          : snapshot.connectedAt == null
            ? {connectedAt: this.deps.now()}
            : {}
      this.patchCall({
        voiceCall,
        direction: "outgoing",
        remoteDisplayName:
          snapshot.remoteDisplayName ?? voiceCall.contact?.name ?? null,
        ...connectedAtPatch,
      })
      if (wasIncoming) {
        void this.answer()
      }
      return
    }

    // No SDK call yet. A LIVE record is the advance notice for
    // an agent leg about to ring — stash it so wireIncomingCalls
    // attaches that leg as outgoing + auto-answers when it rings.
    // A TERMINAL record must instead CLEAR any stale stash:
    // after the local snapshot collapsed (settle timer), the
    // `call.ended` broadcast used to land here and arm the
    // auto-answer with a finished call, hijacking the next
    // genuine incoming call (CON-211).
    if (isTerminalVoiceCall(voiceCall)) {
      this.pendingOutbound = null
      return
    }
    this.pendingOutbound = voiceCall
  }

  // ── Media plumbing ──────────────────────────────────────

  private async ensureMicAttached(
    sdk: VoxSdk,
    call: Call,
  ): Promise<void> {
    const stream = await this.ensureMicStream(sdk)
    try {
      await call.addStream(stream)
    } catch {
      // Already sending audio (e.g. tracker auto-added) —
      // not an error worth surfacing.
    }
  }

  private async ensureMicStream(sdk: VoxSdk): Promise<LocalStream> {
    // Prefer the device tracker's managed stream; fall back
    // to capturing one directly. Either path can throw
    // getUserMedia errors, which we map to humane copy.
    const tracked = this.deviceTracker?.audioStream.value
    if (tracked) {
      this.micStream = tracked
      this.micStreamOwned = false
      return tracked
    }
    if (this.micStream) return this.micStream

    try {
      const stream = await sdk.streamModule.streamManager.createAudioStream(
        { audioProcessing: true },
        this.state.currentMicrophoneId ?? undefined,
      )
      this.micStream = stream
      this.micStreamOwned = true
      this.setState({ mediaError: null })
      return stream
    } catch (error) {
      this.setState({ mediaError: microphoneErrorMessage(error) })
      throw error
    }
  }

  private renderRemoteStreams(
    sdk: VoxSdk,
    streams: Set<RemoteStream>,
  ): void {
    // Add a renderer for each newly-arrived remote stream.
    streams.forEach((stream) => {
      if (this.renderers.has(stream.id)) return
      const renderer = sdk.streamModule.rendererManager.createAudioRenderer(
        stream,
      )
      this.renderers.set(stream.id, renderer)
      this.mountAudioElement(renderer)
    })

    // Tear down any renderer whose stream is no longer present
    // (RemoteMediaRemoved). The v5 migration guide is explicit:
    // remove the renderer when its stream no longer exists, or
    // a dead `<audio>` element lingers. (CON-151)
    const liveIds = new Set<string>()
    streams.forEach((stream) => liveIds.add(stream.id))
    this.renderers.forEach((renderer, id) => {
      if (liveIds.has(id)) return
      this.disposeRenderer(renderer)
      this.renderers.delete(id)
    })

    // Media-health: once the call has received remote media, an
    // empty set means the remote audio leg dropped mid-call —
    // surface "Reconnecting…" rather than a silent live timer.
    // When media returns, the leg recovered → clear the flag.
    if (streams.size > 0) {
      this.hadRemoteMedia = true
      if (this.state.call?.mediaState === "dropped") {
        this.patchCall({ mediaState: "ok" })
      }
    } else if (this.hadRemoteMedia) {
      this.patchCall({ mediaState: "dropped" })
    }
  }

  // Mirror the SDK Call's own state into the snapshot's media
  // health. `Reconnecting` (transport/RTP recovery) reads as a
  // dropped leg; `Connected` clears it once media is flowing
  // again. Other states are owned by the CallEvent handlers.
  private reflectCallState(sdk: VoxSdk, next: CallState): void {
    if (!this.state.call) return
    if (next === sdk.enums.CallState.Reconnecting) {
      this.patchCall({ mediaState: "dropped" })
    } else if (
      next === sdk.enums.CallState.Connected &&
      (this.call?.remoteStreams.value.size ?? 0) > 0
    ) {
      this.patchCall({ mediaState: "ok" })
    }
  }

  private disposeRenderer(renderer: AudioRenderer): void {
    try {
      renderer.clear()
      const element = renderer.getElement()
      element.remove?.()
    } catch {
      // ignore teardown errors
    }
  }

  private mountAudioElement(renderer: AudioRenderer): void {
    let element: HTMLAudioElement
    try {
      element = renderer.getElement()
    } catch {
      return
    }
    element.autoplay = true
    if (typeof document !== "undefined" && !element.isConnected) {
      element.style.display = "none"
      document.body.appendChild(element)
    }
    const playback = element.play?.()
    if (playback && typeof playback.catch === "function") {
      playback.catch((error: unknown) => {
        if (isAutoplayError(error)) {
          this.setState({ mediaError: AUTOPLAY_BLOCKED_MESSAGE })
        }
      })
    }
  }

  // Retry blocked audio playback after a user gesture.
  resumeAudio(): void {
    if (this.state.mediaError !== AUTOPLAY_BLOCKED_MESSAGE) return
    this.renderers.forEach((renderer) => {
      const element = renderer.getElement()
      void element.play?.()
    })
    this.setState({ mediaError: null })
  }

  private failCall(error: unknown): void {
    this.setState({ mediaError: microphoneErrorMessage(error) })
    this.endCall("failed")
  }

  // Clear a terminal call snapshot the user is done with —
  // e.g. a "couldn't be connected" state that stays on screen
  // until the user explicitly dismisses it.
  dismissCall(): void {
    this.clearSettleTimer()
    this.setState({ call: null })
  }

  private clearSettleTimer(): void {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer)
      this.settleTimer = null
    }
  }

  private endCall(phase: "ended" | "failed"): void {
    // A failed/unconnected call STAYS until the user dismisses
    // it — they need to read why it didn't connect. So:
    //   - endCall("failed") locks the snapshot into the failed
    //     terminal and cancels any pending auto-collapse.
    //   - a later endCall("ended") (e.g. the SDK leg's
    //     Disconnected after the scenario terminated) must NOT
    //     downgrade an already-failed call or schedule a clear.
    if (phase === "failed") {
      this.clearSettleTimer()
      if (this.state.call) {
        this.patchCall({ phase: "failed", endReason: "failed" })
      }
      this.detachCall()
      return
    }

    if (this.state.call?.endReason === "failed") {
      this.detachCall()
      return
    }

    const record = this.state.call?.voiceCall
    if (record && !isTerminalVoiceCall(record)) {
      // The SDK ended its leg while the server still says the call
      // is live (CON-211 prod incident: a spurious agent-leg end
      // mid-ring). The UI heals from the terminal `call.ended`
      // broadcast; log so the real trigger is visible next time.
      verr("endCall: SDK leg ended but VoiceCall still live", {
        voiceCallId: record.id,
        status: record.status,
      })
    }

    if (this.state.call) {
      this.patchCall({ phase })
    }
    this.detachCall()
    // Normal completed call: collapse on its own after a beat.
    this.clearSettleTimer()
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null
      this.setState({ call: null })
    }, 1_200)
  }

  private detachCall(): void {
    this.callWatchers.forEach((unwatch) => unwatch())
    this.callWatchers = []
    this.renderers.forEach((renderer) => this.disposeRenderer(renderer))
    this.renderers.clear()
    this.releaseMicStream()
    this.hadRemoteMedia = false
    this.call = null
  }

  // Release the captured microphone when the call tears down.
  // Without this the local mic `LocalStream` stays live after
  // hangup and the browser keeps the tab's mic-in-use indicator
  // lit (CON-169). `LocalStream.close()` stops capturing media
  // and releases the device; we only call it on streams WE
  // created (the tracker owns the lifecycle of its own).
  private releaseMicStream(): void {
    if (this.micStream && this.micStreamOwned) {
      this.micStream.close()
    }
    this.micStream = null
    this.micStreamOwned = false
  }

  // ── Helpers ─────────────────────────────────────────────

  private nodeFor(sdk: VoxSdk, node: number): ConnectionNode {
    const key = `NODE_${node}` as keyof typeof sdk.enums.ConnectionNode
    return (
      sdk.enums.ConnectionNode[key] ?? sdk.enums.ConnectionNode.NODE_1
    )
  }

  private fullUsername(config: VoiceConfig): string {
    // If the server already shipped a FQDN, use it as-is.
    if (config.username.includes("@")) return config.username
    // Otherwise the FQDN can't be assembled without the
    // account name — fall back to the bare value and let the
    // SDK surface an InvalidUsername error if it's wrong.
    return config.username
  }
}

function toMicDevices(
  mics: ReadonlyArray<{ id: string; label: string }>,
): MicDevice[] {
  return mics.map((m) => ({
    id: m.id,
    label: m.label || "Microphone",
  }))
}

function safeDeviceToken(): string {
  try {
    return getDeviceToken()
  } catch {
    return "ephemeral-device"
  }
}

// Default auth-hash fetch: POST the one-time login key to
// the Rails endpoint, which returns the server-computed
// hash (the password never leaves the server).
async function defaultFetchAuthHash(loginKey: string): Promise<string> {
  const response = await fetch("/v2/channels/voice/auth", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({ login_key: loginKey }),
  })

  if (!response.ok) {
    throw new Error(`Voice auth failed (${response.status})`)
  }

  const data = (await response.json()) as { hash?: string }
  if (!data.hash) throw new Error("Voice auth returned no hash")
  return data.hash
}

// Default loader: the REAL Core.init + module wiring. Kept
// out of the class so tests inject a fake without touching
// the SDK module graph. Imported lazily so the SDK bundle
// only loads when an agent actually has voice enabled.
export const defaultSdkLoader: SdkLoader = async () => {
  try {
    const core = await import("@voximplant/websdk")
    const callMod = await import(
      "@voximplant/websdk/modules/call-manager"
    )
    const streamMod = await import("@voximplant/websdk/modules/stream")

    // Keep the SDK's own logger at Warning: real warnings and
    // errors still surface to the console (with the VOXSDK
    // prefix), but the per-second connection ping/pong Debug
    // flood is gone.
    const instance = core.Core.init({
      logger: {
        logLevel: core.LogLevel.Warning,
        enableConsoleLogger: true,
        prefix: "VOXSDK",
      },
    })
    instance.registerModules([
      streamMod.StreamLoader(),
      callMod.CallLoader(),
    ])

    // `getModuleAsync` resolves once registration completes
    // and returns a non-nullable module (the sync
    // `getModule` is `Module | undefined`).
    const callManager = await instance.getModuleAsync(callMod.callToken)
    const streamModule = await instance.getModuleAsync(streamMod.streamToken)

    return {
      client: instance.client,
      callManager,
      streamModule,
      enums: {
        ConnectionNode: core.ConnectionNode,
        ClientEventDisconnected: core.ClientEvent.Disconnected,
        ClientDisconnectConnectionLost:
          core.ClientDisconnectReason.ConnectionLost,
        ClientState: core.ClientState,
        CallManagerIncomingCall: callMod.CallManagerEvent.IncomingCall,
        CallState: callMod.CallState,
        CallEventConnected: callMod.CallEvent.Connected,
        CallEventDisconnected: callMod.CallEvent.Disconnected,
        CallEventFailed: callMod.CallEvent.Failed,
        CallDirectionIncoming: callMod.CallDirection.Incoming,
        RejectModeDecline: callMod.RejectMode.Decline,
        StreamHelperDeviceTracker: streamMod.StreamHelper.DeviceTracker,
      },
    }
  } catch (error) {
    verr("loadSdk: FAILED", error)
    throw error
  }
}

// The process-wide singleton. `useVoximplant` is the only
// consumer; everything else goes through the hook.
let singleton: VoximplantClient | null = null

export function getVoximplantClient(): VoximplantClient {
  if (!singleton) {
    singleton = new VoximplantClient({ loadSdk: defaultSdkLoader })
  }
  return singleton
}

// Test seam: reset the module singleton between specs.
export function __resetVoximplantClientForTests(
  next: VoximplantClient | null,
): void {
  singleton = next
}
